// PGC Predictive Search v20260715075952
// v20260715075025 - price fix
/* cache-bust: 1784020899 */
/**
 * Pengelly Garden Centre — Predictive Search
 * Self-contained, no Dawn JS dependency.
 * Calls /search/suggest.json and renders product images, titles, prices.
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 250;
  const MIN_CHARS = 2;
  const MAX_RESULTS = 8;

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function formatMoney(price) {
    // Shopify suggest API returns price already in pounds (e.g. "7.99"), not pence
    var amount = parseFloat(price);
    if (isNaN(amount)) return '';
    return '£' + amount.toFixed(2);
  }

  function buildResultsHTML(data, query) {
    const products = (data.resources && data.resources.results && data.resources.results.products) || [];
    const queries = (data.resources && data.resources.results && data.resources.results.queries) || [];
    const collections = (data.resources && data.resources.results && data.resources.results.collections) || [];

    if (products.length === 0 && queries.length === 0 && collections.length === 0) {
      return `<div class="pgc-ps-empty">No results found for "<strong>${escapeHTML(query)}</strong>"</div>`;
    }

    let html = '';

    // Suggestions (queries + collections)
    const suggestions = [...queries.slice(0, 3), ...collections.slice(0, 2)];
    if (suggestions.length > 0) {
      html += `<div class="pgc-ps-group">
        <div class="pgc-ps-heading">Suggestions</div>
        <ul class="pgc-ps-list">`;
      suggestions.forEach(item => {
        html += `<li class="pgc-ps-item pgc-ps-item--suggestion">
          <a href="${escapeHTML(item.url)}" class="pgc-ps-link">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <span>${escapeHTML(item.title)}</span>
          </a>
        </li>`;
      });
      html += `</ul></div>`;
    }

    // Products
    if (products.length > 0) {
      html += `<div class="pgc-ps-group">
        <div class="pgc-ps-heading">Products</div>
        <ul class="pgc-ps-list">`;
      products.slice(0, MAX_RESULTS).forEach(product => {
        const imgSrc = product.featured_image && product.featured_image.url
          ? product.featured_image.url + '&width=100'
          : '';
        const imgHTML = imgSrc
          ? `<img src="${escapeHTML(imgSrc)}" alt="${escapeHTML(product.title)}" class="pgc-ps-img" loading="lazy" width="52" height="52">`
          : `<div class="pgc-ps-img pgc-ps-img--placeholder"></div>`;
        const price = product.price ? formatMoney(product.price) : '';
        const comparePrice = product.compare_at_price && product.compare_at_price > product.price
          ? `<s class="pgc-ps-compare">${formatMoney(product.compare_at_price)}</s>` : '';
        html += `<li class="pgc-ps-item">
          <a href="${escapeHTML(product.url)}" class="pgc-ps-link pgc-ps-link--product">
            ${imgHTML}
            <div class="pgc-ps-info">
              <div class="pgc-ps-title">${escapeHTML(product.title)}</div>
              ${product.vendor ? `<div class="pgc-ps-vendor">${escapeHTML(product.vendor)}</div>` : ''}
              <div class="pgc-ps-price">${price}${comparePrice}</div>
            </div>
          </a>
        </li>`;
      });
      html += `</ul></div>`;
    }

    // View all link
    html += `<div class="pgc-ps-footer">
      <a href="/search?q=${encodeURIComponent(query)}&type=product" class="pgc-ps-view-all">
        View all results for "<strong>${escapeHTML(query)}</strong>" →
      </a>
    </div>`;

    return html;
  }

  function escapeHTML(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  async function fetchSuggestions(query, type) {
    const params = new URLSearchParams({
      q: query,
      resources: JSON.stringify({
        type: 'product,collection,query',
        limit: MAX_RESULTS,
        options: { unavailable_products: 'last', fields: 'title,product_type,variants.title,vendor' }
      }),
      'resources[type]': 'product,collection,query',
      'resources[limit]': MAX_RESULTS,
    });
    if (type && type !== '') {
      params.set('resources[type]', type);
    }
    const url = `/search/suggest.json?${params.toString()}`;
    const resp = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
    if (!resp.ok) throw new Error('Search failed');
    return resp.json();
  }

  function initPredictiveSearch() {
    const searchForms = document.querySelectorAll('.pgc-search-bar');
    if (!searchForms.length) return;

    searchForms.forEach(container => {
      const input = container.querySelector('input[type="search"]');
      const typeSelect = container.querySelector('select[name="type"]');
      let resultsDiv = container.querySelector('.pgc-predictive-results');

      if (!input) return;

      // Create results div if not present
      if (!resultsDiv) {
        resultsDiv = document.createElement('div');
        resultsDiv.className = 'pgc-predictive-results';
        resultsDiv.setAttribute('role', 'listbox');
        container.appendChild(resultsDiv);
      }

      function closeResults() {
        resultsDiv.innerHTML = '';
        resultsDiv.style.display = 'none';
        input.setAttribute('aria-expanded', 'false');
      }

      function showResults(html) {
        resultsDiv.innerHTML = html;
        resultsDiv.style.display = 'block';
        input.setAttribute('aria-expanded', 'true');
      }

      const doSearch = debounce(async function () {
        const query = input.value.trim();
        const type = typeSelect ? typeSelect.value : '';

        if (query.length < MIN_CHARS) {
          closeResults();
          return;
        }

        resultsDiv.innerHTML = '<div class="pgc-ps-loading">Searching...</div>';
        resultsDiv.style.display = 'block';

        try {
          const data = await fetchSuggestions(query, type);
          showResults(buildResultsHTML(data, query));
        } catch (e) {
          closeResults();
        }
      }, DEBOUNCE_MS);

      input.addEventListener('input', doSearch);
      input.addEventListener('focus', function () {
        if (input.value.trim().length >= MIN_CHARS) doSearch();
      });

      // Close on outside click
      document.addEventListener('click', function (e) {
        if (!container.contains(e.target)) closeResults();
      });

      // Keyboard navigation
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          closeResults();
          input.blur();
        }
        if (e.key === 'ArrowDown') {
          const first = resultsDiv.querySelector('.pgc-ps-link');
          if (first) { e.preventDefault(); first.focus(); }
        }
      });

      resultsDiv.addEventListener('keydown', function (e) {
        const links = Array.from(resultsDiv.querySelectorAll('.pgc-ps-link'));
        const idx = links.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' && idx < links.length - 1) {
          e.preventDefault(); links[idx + 1].focus();
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (idx > 0) links[idx - 1].focus();
          else input.focus();
        }
        if (e.key === 'Escape') { closeResults(); input.focus(); }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPredictiveSearch);
  } else {
    initPredictiveSearch();
  }
})();
